import React, { useState } from "react";
import "../../styles/ContactUs.css";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import { FaMailBulk, FaPhoneAlt } from "react-icons/fa";
import { create } from "../../api/API";

export default function ContactUs() {
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState([
    { sender: "bot", text: "👋 Hi there! What's your name?" },
  ]);
  const [input, setInput] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    feedback: "",
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle chatbot message flow
  const handleUserResponse = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;

    // Show user message
    setMessages((prev) => [...prev, { sender: "user", text: value }]);
    setInput("");

    if (step === 0) {
      setFormData({ ...formData, name: value });
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: `Nice to meet you, ${value}! What's your email?` },
        ]);
      }, 600);
      setStep(1);
    } else if (step === 1) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        toast.error("Please enter a valid email address.");
        return;
      }
      setFormData({ ...formData, email: value });
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: "Got it! What's your 10-digit phone number?" },
        ]);
      }, 600);
      setStep(2);
    } else if (step === 2) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(value)) {
        toast.error("Please enter a valid 10-digit phone number.");
        return;
      }
      setFormData({ ...formData, phone: value });
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: "Perfect! What message would you like to send?" },
        ]);
      }, 600);
      setStep(3);
    } else if (step === 3) {
      setFormData({ ...formData, feedback: value });
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: "✅ Great! Please verify you’re human below 👇" },
        ]);
      }, 600);
      setStep(4);
    }
  };

  // Handle submission with API call
  const handleSubmit = async () => {
    if (!captchaValue) {
      toast.error("⚠️ Please verify that you are not a robot.");
      return;
    }

    const { name, email, phone, feedback } = formData;

    if (!name || !email || !feedback) {
      toast.error("Please fill all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      await create("/contact-us", { name, email, phone, feedback });
      toast.success("✅ Message sent successfully!");

      setMessages([
        { sender: "bot", text: "🎉 Thanks for reaching out! We'll get back to you soon." },
      ]);
      setFormData({ name: "", email: "", phone: "", feedback: "" });
      setStep(0);
      setCaptchaValue(null);
    } catch (error) {
      console.error(error);
      toast.error(`❌ Submission failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container pt-2 pb-5">
      <div className="row bg-white shadow rounded-4 overflow-hidden">
        {/* Left side - Chat Interface */}
        <div className="col-lg-5 p-4 d-flex justify-content-center align-items-center">
          <div className="chat-screen w-100">
            <div className="chat-wrapper">
              {/* Header */}
              <div className="chat-header" style={{ backgroundColor: "#2c0446" }}>
                <img
                  src="/assets/images/Component 73.svg"
                  alt="bot"
                  className="chat-avatar"
                />
                <div>
                  <p className="chat-name mb-0">WiSE Ally</p>
                  <p className="chat-status mb-0">Online</p>
                </div>
              </div>

              {/* Messages */}
              <div className="chat-messages">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`chat-bubble ${msg.sender === "user" ? "user" : "bot"}`}
                    style={{
                      backgroundColor: msg.sender === "user" ? "#2c0446" : "white",
                    }}
                  >
                    {msg.text}
                  </div>
                ))}
              </div>

              {/* Input or Captcha */}
              {step < 4 ? (
                <form onSubmit={handleUserResponse} className="chat-input">
                  <input
                    type="text"
                    placeholder="Type your reply..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  />
                  <button
                    type="submit"
                    style={{ backgroundColor: "#2c0446", color: "white" }}
                  >
                    Send
                  </button>
                </form>
              ) : (
                <div className="chat-submit">
                  <div className="w-100 text-center">
                    <ReCAPTCHA
                      sitekey="6Le3awwsAAAAAJJXpDi03RuMZOPeWa3dYGAtR9GU"
                      onChange={(value) => setCaptchaValue(value)}
                    />
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: "#2c0446",
                        color: "white",
                        marginTop: "10px",
                      }}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Image and contact info */}
        <div className="col-lg-7 bg-light d-flex flex-column align-items-center justify-content-center p-5 text-center pt-2">
          <div className="bg-white rounded-4 p-4 shadow-sm mb-4 w-100">
            <img
              src="/assets/images/Component 73.svg"
              alt="Contact"
              className="img-fluid rounded-4 ContactUs-right-image"
            />
          </div>

          <div className="w-100">
            <div className="d-flex align-items-center bg-white rounded-3 p-3 shadow-sm mb-3">
              <div
                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{ width: "45px", height: "45px" }}
              >
                <FaMailBulk />
              </div>
              <div className="text-start">
                <h6 className="mb-0 fw-semibold">Email</h6>
                <small className="text-muted">WiSE@gmail.com</small>
              </div>
            </div>

            <div className="d-flex align-items-center bg-white rounded-3 p-3 shadow-sm">
              <div
                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{ width: "45px", height: "45px" }}
              >
                <FaPhoneAlt />
              </div>
              <div className="text-start">
                <h6 className="mb-0 fw-semibold">Phone</h6>
                <small className="text-muted">(+91) 0123456789</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
